/**
 * Build special product-group incentive lines for configured employees.
 */
import { lineAmountOnly } from "../features/salesDataImport/services/salesLineChunksReader";
import { fetchImportGroupSaleTotals } from "./monthlyImportPrecalcService";
import {
  matchPlanProductGroupFromRow,
  resolveEmployeeNamesForPlan,
  sumBranchSaleForPlanGroup,
} from "../config/employeeSpecialIncentivePlans";
import { loadBranchSaleRowsForMonth, rowMatchesSelectedEmployees } from "./monthlyIncentivePartySalesService";

function buildLinesFromGroupSales(plan, groupSaleRows, employeeName) {
  const employeeId = String(plan.employeeId).trim();
  const groups = (plan.groups || []).map((g) => String(g).trim()).filter(Boolean);
  const ratePct = Number(plan.ratePct);
  const pct = Number.isFinite(ratePct) && ratePct > 0 ? ratePct : 1;

  return groups.map((groupId) => {
    const saleAmount = sumBranchSaleForPlanGroup(groupId, groupSaleRows);
    return {
      employeeId,
      employeeName: employeeName || employeeId,
      groupId,
      saleAmount,
      ratePct: pct,
      incentiveAmount: saleAmount * (pct / 100),
      isSpecialGroupPlan: true,
    };
  });
}

function buildLinesFromTaggedRows(plan, saleRows, employeeNames) {
  const groups = (plan.groups || []).map((g) => String(g).trim()).filter(Boolean);
  const ratePct = Number(plan.ratePct);
  const pct = Number.isFinite(ratePct) && ratePct > 0 ? ratePct : 1;
  const saleByGroup = Object.fromEntries(groups.map((g) => [g, 0]));

  for (const row of saleRows) {
    if (!rowMatchesSelectedEmployees(row, employeeNames)) continue;
    const groupId = matchPlanProductGroupFromRow(row, groups);
    if (!groupId) continue;
    const amt = Math.abs(lineAmountOnly(row));
    if (amt > 0) saleByGroup[groupId] += amt;
  }

  const employeeId = String(plan.employeeId).trim();
  const employeeName = employeeNames[0] || employeeId;
  return groups.map((groupId) => {
    const saleAmount = Number(saleByGroup[groupId]) || 0;
    return {
      employeeId,
      employeeName,
      groupId,
      saleAmount,
      ratePct: pct,
      incentiveAmount: saleAmount * (pct / 100),
      isSpecialGroupPlan: true,
    };
  });
}

export function buildSpecialGroupIncentiveLines(plan, ctx) {
  if (!plan) return [];
  const employeeId = String(plan.employeeId).trim();
  const employeeNames = resolveEmployeeNamesForPlan(employeeId, ctx.employees, ctx.staffRows);
  const employeeName = employeeNames[0] || employeeId;

  if (plan.useBranchGroupSales !== false) {
    return buildLinesFromGroupSales(plan, ctx.groupSaleRows || [], employeeName);
  }
  return buildLinesFromTaggedRows(plan, ctx.saleRows || [], employeeNames);
}

export async function fetchSpecialGroupIncentiveBundle({
  year,
  monthIndex,
  branchFilter = "Main",
  plans = [],
  employees = [],
  staffRows = [],
  /** When set (including []), skips a separate precalc / live group fetch. */
  preloadedGroupSaleRows,
}) {
  const activePlans = (plans || []).filter((p) => String(p?.employeeId ?? "").trim());
  if (!activePlans.length) {
    return { lines: [], totalSale: 0, totalIncentive: 0, groupSaleRows: [] };
  }

  const needsBranchGroups = activePlans.some((p) => p.useBranchGroupSales !== false);
  const needsTaggedRows = activePlans.some((p) => p.useBranchGroupSales === false);

  const [groupSaleRows, saleRows] = await Promise.all([
    preloadedGroupSaleRows !== undefined
      ? Promise.resolve(preloadedGroupSaleRows)
      : needsBranchGroups
        ? fetchImportGroupSaleTotals(year, monthIndex, branchFilter)
        : Promise.resolve([]),
    needsTaggedRows ? loadBranchSaleRowsForMonth(year, monthIndex, branchFilter) : Promise.resolve([]),
  ]);

  const ctx = { year, monthIndex, branchFilter, employees, staffRows, groupSaleRows, saleRows };
  const lines = activePlans.flatMap((plan) => buildSpecialGroupIncentiveLines(plan, ctx));
  const totalSale = lines.reduce((a, L) => a + L.saleAmount, 0);
  const totalIncentive = lines.reduce((a, L) => a + L.incentiveAmount, 0);
  return { lines, totalSale, totalIncentive, groupSaleRows };
}
